import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { PRO_PRICES, detectCurrency, formatPrice, getStripePlanKey } from '../data/pricing';
import { trackProUpgradeStarted } from '../utils/analytics';
import {
  Check, X, Crown, Sparkles, ArrowRight,
  Download, Palette, Zap, Lock, Infinity, Image
} from 'lucide-react';

const COMPARISON_ROWS = [
  {
    label: 'AI generations per month',
    free: '3 per month',
    pro: 'Unlimited',
    icon: Zap,
  },
  {
    label: 'Product access',
    free: 'All 32+ templates',
    pro: 'All 32+ templates',
    icon: Sparkles,
    bothIncluded: true,
  },
  {
    label: 'Premium preview',
    free: '2-3 pages, then paywall',
    pro: 'Full access to all pages',
    icon: Lock,
  },
  {
    label: 'Export (PNG)',
    free: 'Standard quality',
    pro: 'High quality',
    icon: Image,
  },
  {
    label: 'Export (PDF)',
    free: false,
    pro: 'Print-ready A4/Letter',
    icon: Download,
  },
  {
    label: 'Premium design templates',
    free: false,
    pro: true,
    icon: Palette,
  },
  {
    label: 'Pinterest Pin Creator',
    free: false,
    pro: true,
    icon: Image,
  },
  {
    label: 'Collections (bundle access)',
    free: 'Individual products only',
    pro: 'All collections included',
    icon: Sparkles,
  },
  {
    label: 'Commercial License',
    free: false,
    pro: true,
    icon: Check,
  },
  {
    label: 'Priority support',
    free: false,
    pro: true,
    icon: Crown,
  },
];

export const FreeVsProComparison = ({ context = 'page', className = '' }) => {
  const navigate = useNavigate();
  const { isAuthenticated, isPro } = useAuth();
  const [interval, setInterval] = useState('monthly');
  const currency = detectCurrency();

  const price = interval === 'yearly' ? PRO_PRICES.yearly[currency] : PRO_PRICES.monthly[currency];
  const monthlyEquiv = interval === 'yearly'
    ? (currency === 'nok' ? PRO_PRICES.yearly.monthlyEquivalentNok : PRO_PRICES.yearly.monthlyEquivalentUsd)
    : null;
  const priceLabel = formatPrice(price, currency);
  const periodLabel = interval === 'yearly' ? '/yr' : '/mo';

  const handleUpgrade = () => {
    trackProUpgradeStarted(interval, price, currency);
    if (!isAuthenticated) {
      navigate('/login?redirect=/pricing');
      return;
    }
    navigate(`/pricing?interval=${interval}`);
  };

  if (isPro) return null;

  const isCompact = context === 'inline';

  return (
    <div data-testid="free-vs-pro-comparison" className={`${className}`}>
      {!isCompact && (
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-50 to-amber-100 text-amber-800 text-[11px] font-sans font-bold uppercase tracking-wider mb-4 border border-amber-200/60">
            <Crown className="w-3.5 h-3.5" />
            Free vs Pro
          </div>
          <h3 className="text-xl md:text-2xl font-serif font-bold text-[#111111] tracking-tight">
            Compare plans
          </h3>
        </div>
      )}

      {/* Interval toggle */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex items-center gap-1 bg-stone-50 rounded-full p-1 border border-stone-100">
          <button
            data-testid="compare-monthly-btn"
            onClick={() => setInterval('monthly')}
            className={`px-4 py-1.5 rounded-full text-xs font-sans font-bold transition-all ${
              interval === 'monthly' ? 'bg-stone-900 text-white shadow-sm' : 'text-stone-500'
            }`}
          >
            Monthly
          </button>
          <button
            data-testid="compare-yearly-btn"
            onClick={() => setInterval('yearly')}
            className={`px-4 py-1.5 rounded-full text-xs font-sans font-bold transition-all relative ${
              interval === 'yearly' ? 'bg-stone-900 text-white shadow-sm' : 'text-stone-500'
            }`}
          >
            Yearly
            <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
              -{PRO_PRICES.yearly.savingsPercent}%
            </span>
          </button>
        </div>
      </div>

      {/* Comparison table */}
      <div className="relative pt-4">
        {/* Recommended badge */}
        <div className="absolute top-0 right-0 w-1/3 flex justify-center z-10">
          <span className="text-[9px] font-sans font-bold px-3 py-1 rounded-full bg-amber-400 text-amber-900 uppercase tracking-wider shadow-sm">
            Recommended
          </span>
        </div>
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-x-auto">
        {/* Header */}
        <div className="grid grid-cols-3 border-b border-stone-100 min-w-[480px]">
          <div className="p-4" />
          <div className="p-4 text-center border-l border-stone-100">
            <p className="text-xs font-sans font-bold text-stone-500 uppercase tracking-wider">Free</p>
            <p className="text-lg font-serif font-bold text-stone-400 mt-1">{formatPrice(0, currency)}</p>
          </div>
          <div className="p-4 pt-5 text-center bg-stone-900 text-white rounded-tr-2xl relative">
            <p className="text-xs font-sans font-bold text-stone-300 uppercase tracking-wider mt-1">Pro</p>
            <p className="text-lg font-serif font-bold text-white mt-1">{priceLabel}<span className="text-xs font-sans text-stone-400">{periodLabel}</span></p>
            {monthlyEquiv && (
              <p className="text-[10px] font-sans text-emerald-400 mt-0.5">
                = {formatPrice(monthlyEquiv, currency)}/mo
              </p>
            )}
          </div>
        </div>

        {/* Rows */}
        {COMPARISON_ROWS.map((row, i) => {
          const Icon = row.icon;
          return (
            <div
              key={i}
              className={`grid grid-cols-3 min-w-[480px] ${i < COMPARISON_ROWS.length - 1 ? 'border-b border-stone-50' : ''} ${i % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}`}
            >
              <div className="p-3.5 flex items-center gap-2.5">
                <Icon className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-xs font-sans text-stone-700">{row.label}</span>
              </div>
              <div className="p-3.5 text-center border-l border-stone-100 flex items-center justify-center">
                {row.bothIncluded ? (
                  <span className="text-xs font-sans text-stone-600">{row.free}</span>
                ) : row.free === false ? (
                  <X className="w-4 h-4 text-stone-300" />
                ) : (
                  <span className="text-xs font-sans text-stone-500">{row.free}</span>
                )}
              </div>
              <div className="p-3.5 text-center bg-stone-900/[0.03] flex items-center justify-center">
                {row.pro === true ? (
                  <Check className="w-4 h-4 text-emerald-500" />
                ) : (
                  <span className="text-xs font-sans font-medium text-stone-800">{row.pro}</span>
                )}
              </div>
            </div>
          );
        })}

        {/* CTA */}
        <div className="grid grid-cols-3 border-t border-stone-100 min-w-[480px]">
          <div className="p-4" />
          <div className="p-4 text-center border-l border-stone-100">
            {!isAuthenticated && (
              <Button
                data-testid="compare-free-btn"
                variant="outline"
                onClick={() => navigate('/register')}
                className="rounded-full px-5 py-4 text-xs font-sans font-semibold border-stone-200 text-stone-600"
              >
                Get started free
              </Button>
            )}
          </div>
          <div className="p-4 text-center">
            <Button
              data-testid="compare-upgrade-btn"
              onClick={handleUpgrade}
              className="rounded-full px-5 py-4 text-xs font-sans font-bold bg-stone-900 text-white hover:bg-stone-800 shadow-md"
            >
              <Crown className="w-3.5 h-3.5 mr-1.5" />
              Upgrade to Pro
              <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        </div>
      </div>
      </div>{/* close relative pt-4 wrapper */}
    </div>
  );
};
